import {
    type AgentSubscriber,
    HttpAgent,
    type HttpAgentFetchFn,
    type RunAgentInput,
    type RunAgentParameters,
    type RunAgentResult,
    type Tool,
} from '@ag-ui/client';

import type { RequestHeaders } from '~/shared/http-fetcher/http-fetcher';

import { agUiHistoryRoles, isAgUiSyncRun } from '../data-contract/chat';
import { parseRetryAfterSeconds } from './busy-retry';

export type CobrainerChatAgentConfig = {
    readonly fetch?: HttpAgentFetchFn;
    /** Resolved before every request, so each run carries the current Authorization header. */
    readonly getRequestHeaders: () => Promise<RequestHeaders>;
    readonly threadId: string;
    /** Declared on every run, next to the tools a run brings itself; a run's own tool wins on a shared name. */
    readonly tools?: readonly Tool[];
    readonly url: string;
};

type LatestResponse = { retryAfterSeconds: number | null };

const HTTP_TOO_MANY_REQUESTS = 429;

const withRequestHeaders =
    (
        fetch: HttpAgentFetchFn,
        getRequestHeaders: () => Promise<RequestHeaders>,
        latest: LatestResponse,
    ): HttpAgentFetchFn =>
    async (url, init) => {
        const response = await fetch(url, { ...init, headers: { ...(await getRequestHeaders()), ...init.headers } });
        latest.retryAfterSeconds =
            response.status === HTTP_TOO_MANY_REQUESTS
                ? parseRetryAfterSeconds(response.headers.get('Retry-After'))
                : null;
        return response;
    };

export class CobrainerChatAgent extends HttpAgent {
    private readonly latest: LatestResponse;
    private readonly tools: readonly Tool[];

    constructor({ fetch, getRequestHeaders, threadId, tools = [], url }: CobrainerChatAgentConfig) {
        const latest: LatestResponse = { retryAfterSeconds: null };
        super({
            fetch: withRequestHeaders(
                fetch ?? ((input, init) => globalThis.fetch(input, init)),
                getRequestHeaders,
                latest,
            ),
            threadId,
            url,
        });
        this.latest = latest;
        this.tools = tools;
    }

    /** The Retry-After of the latest response when it was a 429 another run's lock caused, else null. */
    get retryAfterSeconds(): number | null {
        return this.latest.retryAfterSeconds;
    }

    // Every run shows the producer the conversation the agent holds, oldest first: a turn ends with its
    // new user message, every other run shows the history as it is. Only the conversation and the tools travel.
    protected override requestInit(input: RunAgentInput): RequestInit {
        const messages = input.messages.filter((message) => agUiHistoryRoles.has(message.role));
        const ownNames = new Set(input.tools.map(({ name }) => name));
        const tools = [...input.tools, ...this.tools.filter(({ name }) => !ownNames.has(name))];
        return { ...super.requestInit({ ...input, messages, state: {}, tools }), credentials: 'include' };
    }

    // HttpAgent refreshes its abort controller only in runAgent, so abortRun would otherwise
    // target the previous run instead of this sync run.
    override connectAgent(parameters?: RunAgentParameters, subscriber?: AgentSubscriber): Promise<RunAgentResult> {
        this.abortController = new AbortController();
        return super.connectAgent(parameters, subscriber);
    }

    /** Reads the thread with a sync run on the run endpoint; connectAgent never carries a command. */
    protected override connect(input: RunAgentInput): ReturnType<HttpAgent['run']> {
        const isHeld = (messageId: string): boolean => this.messages.some(({ id }) => id === messageId);
        if (!isAgUiSyncRun(input, isHeld)) {
            throw new Error('connectAgent only reads the thread: it carries no command or resume entries');
        }
        return this.run(input);
    }
}
